import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { Search } from "lucide-react";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const activeCat = params.get("category") || "All";

  const fetchListings = () => {
    setLoading(true);
    const q = {};
    if (params.get("search")) q.search = params.get("search");
    if (params.get("category")) q.category = params.get("category");
    if (params.get("type")) q.listing_type = params.get("type");
    api.get("/listings", { params: q }).then((r) => { setListings(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { api.get("/categories").then((r) => setCats(["All", ...r.data])); }, []);
  useEffect(fetchListings, [params]);

  const submitSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set("search", search); else p.delete("search");
    setParams(p);
  };

  const selectCat = (c) => {
    const p = new URLSearchParams(params);
    if (c === "All") p.delete("category"); else p.set("category", c);
    setParams(p);
  };

  const activeType = params.get("type") || "all";
  const selectType = (t) => {
    const p = new URLSearchParams(params);
    if (t === "all") p.delete("type"); else p.set("type", t);
    setParams(p);
  };
  const TYPE_TABS = [{ k: "all", l: "All" }, { k: "rent", l: "For Rent" }, { k: "sale", l: "For Sale" }];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <h1 className="font-display text-4xl font-bold tracking-tight">Browse gear</h1>
      <p className="text-muted-foreground mt-1">Find the right tool for the job.</p>

      <form onSubmit={submitSearch} className="mt-6 flex items-center gap-2 bg-white border border-black/15 rounded-full p-1.5 max-w-lg">
        <Search className="w-5 h-5 ml-3 text-muted-foreground shrink-0" />
        <input
          data-testid="browse-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items…"
          className="flex-1 bg-transparent outline-none text-sm px-1 min-w-0"
        />
        <button data-testid="browse-search-btn" className="bg-[#0A0A0A] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#FF5A00] transition-colors">Search</button>
      </form>

      <div className="flex items-center gap-2 mt-6 p-1 bg-[#E3E8E1] rounded-full w-fit">
        {TYPE_TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`type-tab-${t.k}`}
            onClick={() => selectType(t.k)}
            className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeType === t.k ? "bg-white shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 no-scrollbar">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`filter-cat-${c}`}
            onClick={() => selectCat(c)}
            className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-colors ${
              activeCat === c ? "bg-black text-white border-black" : "bg-white border-black/15 hover:border-black"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="py-20 flex justify-center"><div className="w-10 h-10 border-4 border-[#FF5A00] border-t-transparent rounded-full animate-spin" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-black/10">
            <p data-testid="no-results" className="text-muted-foreground">No items match your search.</p>
          </div>
        ) : (
          <div data-testid="listings-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
